import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useMemo, useRef, useState } from "react";
import { showNotification } from "context/notification.service";
import { planningTemplatesApi } from "@services/api";
import { PlanningModel } from "types/planning";
import {
  PlanningTemplate,
  PlanningTemplateSection,
} from "types/planning_template";
import { PLANNING_SECTIONS, MODEL_WEEK_COUNTS } from "../../utils/planning_constants";

// ─── Week column helpers (same as PlanningTable) ─────────────────────────────

const FRENCH_MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

interface WeekColumn {
  index: number;
  isoWeek: number;
  month: number;
  monthLabel: string;
  year: number;
  isBlocked: boolean;
}

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((d.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
}

const MODELS_WITHOUT_CONGES = new Set(["12_mois_ordre"]);

function computeWeekColumns(startDate: Date, model: string): WeekColumn[] {
  const n = MODEL_WEEK_COUNTS[model] ?? 48;
  const hasConges = !MODELS_WITHOUT_CONGES.has(model);
  return Array.from({ length: n }, (_, i) => {
    const monday = new Date(startDate.getTime() + i * 7 * 86400000);
    const isoWeek = getISOWeek(monday);
    const month = monday.getMonth() + 1;
    return {
      index: i,
      isoWeek,
      month,
      monthLabel: FRENCH_MONTHS[month - 1],
      year: monday.getFullYear(),
      isBlocked:
        hasConges &&
        ((month === 8 && monday.getDate() <= 28) ||
          (month === 12 && monday.getDate() >= 20)),
    };
  });
}

interface MonthGroup {
  label: string;
  year: number;
  colSpan: number;
}

function buildMonthGroups(cols: WeekColumn[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (const col of cols) {
    const last = groups[groups.length - 1];
    if (last && last.label === col.monthLabel && last.year === col.year) {
      last.colSpan++;
    } else {
      groups.push({ label: col.monthLabel, year: col.year, colSpan: 1 });
    }
  }
  return groups;
}

// Reference start date: Monday of ISO week 1 of 2026
const REFERENCE_START = new Date(2025, 11, 29); // Monday 2025-12-29 = ISO week 1 of 2026

// ─── Model options ───────────────────────────────────────────────────────────

const modelOptions = [
  { value: "11_mois", label: "11 mois (48 sem.)" },
  { value: "12_mois", label: "12 mois (52 sem.)" },
  { value: "12_mois_ordre", label: "12 mois ordre (52 sem.)" },
  { value: "13_mois_avec_conges", label: "13 mois (56 sem.)" },
  { value: "14_mois_avec_conges", label: "14 mois (60 sem.)" },
  { value: "15_mois_avec_conges", label: "15 mois (64 sem.)" },
];

function buildEmptySections(): PlanningTemplateSection[] {
  return PLANNING_SECTIONS.map((sectionDef) => ({
    section_key: sectionDef.key,
    steps: sectionDef.steps.map((step) => ({
      step_key: step.key,
      default_weeks: [],
    })),
  }));
}

// ─── Component ───────────────────────────────────────────────────────────────

const COL_W = 28;
const LABEL_W = 260;

export const PlanningTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const planningTemplates = useProjectStore(
    (state) => state.planningTemplates,
  );
  const userData = useDataStore((state) => state.userData);

  const existingTemplate = planningTemplates.find((t) => t.id === templateId);
  const isNew = !existingTemplate;

  const [name, setName] = useState(existingTemplate?.name || "");
  const [model, setModel] = useState<PlanningModel>(
    existingTemplate?.model || "12_mois",
  );
  const [sections, setSections] = useState<PlanningTemplateSection[]>(
    existingTemplate
      ? structuredClone(existingTemplate.sections)
      : buildEmptySections(),
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Drag-to-paint state
  const isDragging = useRef(false);
  const dragAction = useRef<"add" | "remove">("add");

  // Compute week columns from model
  const cols = useMemo(
    () => computeWeekColumns(REFERENCE_START, model),
    [model],
  );
  const monthGroups = useMemo(() => buildMonthGroups(cols), [cols]);

  const toggleWeek = (
    sectionKey: string,
    stepKey: string,
    week: number,
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        return {
          ...s,
          steps: s.steps.map((st) => {
            if (st.step_key !== stepKey) return st;
            const has = st.default_weeks.includes(week);
            return {
              ...st,
              default_weeks: has
                ? st.default_weeks.filter((w) => w !== week)
                : [...st.default_weeks, week].sort((a, b) => a - b),
            };
          }),
        };
      }),
    );
  };

  const setWeek = (
    sectionKey: string,
    stepKey: string,
    week: number,
    action: "add" | "remove",
  ) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.section_key !== sectionKey) return s;
        return {
          ...s,
          steps: s.steps.map((st) => {
            if (st.step_key !== stepKey) return st;
            if (action === "add" && !st.default_weeks.includes(week)) {
              return {
                ...st,
                default_weeks: [...st.default_weeks, week].sort(
                  (a, b) => a - b,
                ),
              };
            }
            if (action === "remove" && st.default_weeks.includes(week)) {
              return {
                ...st,
                default_weeks: st.default_weeks.filter((w) => w !== week),
              };
            }
            return st;
          }),
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({
        type: "error",
        message: "Le nom du modèle est obligatoire",
      });
      return;
    }

    if (isNew) {
      try {
        const result = await planningTemplatesApi.create({
          name,
          org_id: userData?.org_id || "",
          model,
          sections,
        });
        useProjectStore.getState().addPlanningTemplate(result);
        showNotification({
          type: "success",
          message: "Modèle créé avec succès",
        });
        onClose();
      } catch (error) {
        console.error("Error creating planning template:", error);
        showNotification({
          type: "error",
          message: "Erreur lors de la création du modèle",
        });
      }
    } else {
      const updated: PlanningTemplate = {
        ...existingTemplate,
        name,
        model,
        sections,
      };
      useProjectStore.getState().updatePlanningTemplate(updated);
      showNotification({ type: "success", message: "Modèle enregistré" });
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!existingTemplate) return;
    try {
      useProjectStore
        .getState()
        .removePlanningTemplates([existingTemplate.id]);
      showNotification({ type: "success", message: "Modèle supprimé" });
      onClose();
    } catch (error) {
      console.error("Error deleting planning template:", error);
    }
  };

  const handleDuplicate = async () => {
    if (!existingTemplate) return;
    try {
      const result = await planningTemplatesApi.create({
        name: `Copie de ${name}`,
        org_id: userData?.org_id || "",
        model,
        sections: structuredClone(sections),
      });
      useProjectStore.getState().addPlanningTemplate(result);
      showNotification({
        type: "success",
        message: "Modèle dupliqué avec succès",
      });
      onClose();
    } catch (error) {
      console.error("Error duplicating planning template:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la duplication du modèle",
      });
    }
  };

  return (
    <>
      <BaseModal
        title={
          isNew
            ? "Nouveau modèle de planning"
            : "Modifier le modèle de planning"
        }
        startIcon={<Icon1 name="planning" size={20} />}
        onClose={onClose}
        width="max-w-7xl"
      >
        <div className="bg-white flex flex-col gap-4 p-4">
          <div className="flex flex-row gap-4">
            <div className="flex-1">
              <TextField
                label="Nom du modèle"
                placeholder="Construction maison individuelle"
                value={name}
                onChange={(value) => setName(value)}
              />
            </div>
            <div className="w-250">
              <DropDown
                label="Durée"
                options={modelOptions}
                value={model}
                onSelect={(val) => setModel(val as PlanningModel)}
                category="Modèle"
              />
            </div>
          </div>

          {/* Week grid — same layout as PlanningTable */}
          <div
            className="overflow-auto max-h-[60vh] rounded-xl border border-neutral-200"
            onMouseUp={() => { isDragging.current = false; }}
            onMouseLeave={() => { isDragging.current = false; }}
          >
            <table
              className="border-separate border-spacing-0"
              style={{ minWidth: LABEL_W + cols.length * COL_W }}
            >
              <thead className="sticky top-0 z-30">
                {/* Row 1 — months (merged) */}
                <tr>
                  <td
                    className="sticky left-0 z-40 bg-neutral-100 border border-neutral-200 px-2 py-1 text-h4 text-neutral-500"
                    style={{ width: LABEL_W, minWidth: LABEL_W }}
                  >
                    Mois
                  </td>
                  {monthGroups.map((g, i) => (
                    <td
                      key={i}
                      className="relative z-30 bg-neutral-100 border border-neutral-200 text-h4 font-medium text-center"
                      colSpan={g.colSpan}
                      style={{ minWidth: g.colSpan * COL_W }}
                    >
                      {g.label}
                    </td>
                  ))}
                </tr>

                {/* Row 2 — ISO week numbers */}
                <tr>
                  <td
                    className="sticky left-0 z-40 bg-neutral-50 border border-neutral-200 px-2 py-1 text-h4 text-neutral-400"
                    style={{ width: LABEL_W, minWidth: LABEL_W }}
                  >
                    Semaine
                  </td>
                  {cols.map((col) => (
                    <td
                      key={col.index}
                      className={`relative z-30 border border-neutral-200 text-center text-h4 font-medium ${
                        col.isBlocked
                          ? "bg-gray-300 text-gray-400"
                          : "bg-neutral-50 text-neutral-600"
                      }`}
                      style={{ width: COL_W, minWidth: COL_W }}
                    >
                      {col.isoWeek}
                    </td>
                  ))}
                </tr>
              </thead>

              <tbody>
                {PLANNING_SECTIONS.map((sectionDef) => {
                  const sectionState = sections.find(
                    (s) => s.section_key === sectionDef.key,
                  );

                  return (
                    <TemplateSectionRows
                      key={sectionDef.key}
                      sectionDef={sectionDef}
                      sectionState={sectionState}
                      cols={cols}
                      isDragging={isDragging}
                      dragAction={dragAction}
                      onToggleWeek={toggleWeek}
                      onSetWeek={setWeek}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-t-1 p-4 flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            {!isNew && (
              <SecondaryButton
                variant="small"
                danger={true}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Supprimer
              </SecondaryButton>
            )}
            {!isNew && (
              <SecondaryButton
                variant="small"
                startIcon={<Icon1 name="copy" size={20} />}
                onClick={handleDuplicate}
              >
                Dupliquer
              </SecondaryButton>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <SecondaryButton variant="small" onClick={onClose}>
              Annuler
            </SecondaryButton>
            <Button variant="small" label="Enregistrer" onClick={handleSave} />
          </div>
        </div>
      </BaseModal>

      {showDeleteConfirm && (
        <ConfirmationModal
          title="Supprimer ce modèle de planning ?"
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

// ─── Section rows sub-component ──────────────────────────────────────────────

interface TemplateSectionRowsProps {
  sectionDef: (typeof PLANNING_SECTIONS)[number];
  sectionState: PlanningTemplateSection | undefined;
  cols: WeekColumn[];
  isDragging: React.MutableRefObject<boolean>;
  dragAction: React.MutableRefObject<"add" | "remove">;
  onToggleWeek: (sectionKey: string, stepKey: string, week: number) => void;
  onSetWeek: (
    sectionKey: string,
    stepKey: string,
    week: number,
    action: "add" | "remove",
  ) => void;
}

function TemplateSectionRows({
  sectionDef,
  sectionState,
  cols,
  isDragging,
  dragAction,
  onToggleWeek,
  onSetWeek,
}: TemplateSectionRowsProps) {
  return (
    <>
      {/* Section header row */}
      <tr>
        <td
          className={`sticky left-0 z-10 px-2 py-1 text-h3 font-bold ${sectionDef.headerBg} ${sectionDef.headerText}`}
          style={{ width: LABEL_W, minWidth: LABEL_W }}
        >
          {sectionDef.label}
        </td>
        {cols.map((col) => (
          <td
            key={col.index}
            className={`border-b border-neutral-200 ${
              col.isBlocked
                ? "bg-gray-300"
                : sectionDef.headerBg + " opacity-30"
            }`}
            style={{ width: COL_W }}
          />
        ))}
      </tr>

      {/* Step rows */}
      {sectionDef.steps.map((stepDef) => {
        const stepState = sectionState?.steps.find(
          (st) => st.step_key === stepDef.key,
        );
        const selectedWeeks = stepState?.default_weeks || [];

        return (
          <tr key={stepDef.key}>
            <td
              className="sticky left-0 z-10 px-3 py-1 text-h4 text-neutral-500 bg-white border-b border-neutral-100"
              style={{ width: LABEL_W, minWidth: LABEL_W }}
            >
              {stepDef.label}
            </td>
            {cols.map((col) => {
              const isSelected = selectedWeeks.includes(col.index);
              return (
                <td
                  key={col.index}
                  className={`border-b border-neutral-100 transition-colors ${
                    col.isBlocked
                      ? "bg-gray-200 cursor-not-allowed"
                      : isSelected
                        ? `${sectionDef.cellColor} cursor-pointer`
                        : "bg-white hover:bg-neutral-50 cursor-pointer"
                  }`}
                  style={{ width: COL_W, height: 24 }}
                  title={
                    col.isBlocked
                      ? "Congés"
                      : `S${col.isoWeek} — ${col.monthLabel}`
                  }
                  onMouseDown={(e) => {
                    if (col.isBlocked) return;
                    e.preventDefault();
                    isDragging.current = true;
                    dragAction.current = isSelected ? "remove" : "add";
                    onToggleWeek(sectionDef.key, stepDef.key, col.index);
                  }}
                  onMouseEnter={() => {
                    if (!isDragging.current || col.isBlocked) return;
                    onSetWeek(
                      sectionDef.key,
                      stepDef.key,
                      col.index,
                      dragAction.current,
                    );
                  }}
                  onMouseUp={() => {
                    isDragging.current = false;
                  }}
                />
              );
            })}
          </tr>
        );
      })}
    </>
  );
}
